import { useEffect, useState, Fragment } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Download, ChevronDown } from "lucide-react";
import { money } from "../../lib/cart";
import { toast } from "sonner";
import { ORDER_STATUSES } from "../../lib/orders";
import { OrderDetail } from "./OrderDetail";

export default function AdminOrders() {
  const { getToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const token = await getToken();
    const r = await api.get("/admin/orders", { params: filter ? { status: filter } : {}, headers: { Authorization: `Bearer ${token}` } });
    setOrders(r.data || []);
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (o, status) => {
    const body = { status };
    if (status === "paid") {
      if (!window.confirm(`Confirm you have received payment of ${money(o.total_cents || o.subtotal_cents)} for ${o.order_number}?`)) return load();
      body.confirm_payment = true;
    }
    try {
      const token = await getToken();
      await api.patch(`/admin/orders/${o.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    load();
  };

  const addTracking = async (id) => {
    const t = prompt("Tracking number?");
    if (!t) return;
    try {
      const token = await getToken();
      await api.patch(`/admin/orders/${id}`, { tracking_number: t }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tracking saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save tracking");
    }
    load();
  };

  const exportCSV = async () => {
    const token = await getToken();
    const r = await api.get("/admin/orders/export.csv", { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "kocm_orders.csv"; a.click();
  };

  return (
    <div className="p-8 lg:p-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Fulfillment</p>
          <h1 className="font-serif text-4xl mt-2">Orders</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-[#A8B59A]/30 px-5 py-2 text-sm bg-white" data-testid="order-filter">
            <option value="">All</option>
            {ORDER_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Button onClick={exportCSV} data-testid="export-csv" className="rounded-full bg-[#A8B59A] hover:bg-[#8F9C82] text-[#2B3024]"><Download size={14} className="mr-2" />Pirate Ship CSV</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#5E4D5A] border-b border-[#A8B59A]/20">
              <th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Payment pref.</th>
              <th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4">Tracking</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#5E4D5A]" data-testid="orders-empty">No orders yet.</td></tr>}
            {orders.map((o) => (
              <Fragment key={o.id}>
              <tr data-testid={`order-row-${o.id}`} className="border-b border-[#A8B59A]/10">
                <td className="p-4">
                  <button onClick={() => setOpen(open === o.id ? null : o.id)} className="inline-flex items-center gap-1 font-mono text-xs whitespace-nowrap hover:text-[#59394F]" data-testid={`order-toggle-${o.id}`}>
                    <ChevronDown size={12} className={open === o.id ? "rotate-180 transition-transform" : "transition-transform"} />{o.order_number || o.id.slice(0, 8)}
                  </button>
                  <div className="text-[10px] text-[#5E4D5A] mt-1">{new Date(o.created_at).toLocaleDateString()}</div>
                  {o.delivery_method === "meetup" && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#59394F]/10 text-[#59394F]" data-testid={`order-meetup-badge-${o.id}`}>Local Meetup</span>}
                </td>
                <td className="p-4">{o.customer_name}<br/><span className="text-xs text-[#5E4D5A]">{o.customer_email}</span></td>
                <td className="p-4 text-[#5E4D5A]">{o.payment_preference_label || "—"}</td>
                <td className="p-4 font-medium">{money(o.total_cents || o.subtotal_cents)}</td>
                <td className="p-4">
                  <select value={o.status} onChange={(e) => updateStatus(o, e.target.value)} className="text-xs bg-[#F4EBDD] border border-[#A8B59A]/30 rounded-full px-3 py-1" data-testid={`status-${o.id}`}>
                    {ORDER_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </td>
                <td className="p-4 text-xs text-[#5E4D5A]">{o.tracking_number || "—"}</td>
                <td className="p-4"><button onClick={() => addTracking(o.id)} className="text-xs text-[#59394F] hover:underline" data-testid={`tracking-${o.id}`}>+ tracking</button></td>
              </tr>
              {open === o.id && <tr><td colSpan={7} className="p-0"><OrderDetail o={o} /></td></tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
